import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import omit from "lodash/omit";

import { DASHBOARD_DATE_RANGE_TYPES } from "src/constants";
import { addTrackers } from "src/store/trackers/trackersSlice";
import { selectUserId, selectCompanyId } from "src/store/selectors";
import { addTrackersCollections } from "../trackersCollections/trackersCollectionsSlice";
import { createDashboardDefaultDateRanges } from "src/store/dashboardDateRanges/dashboardDateRangesSlice";
import { onStateFulfilled, onStatePending, onStateRejected } from "../utils";

// Inner imports;
import * as api from "./dashboardsApi";
import { createDashboardPayload } from "./utils";

export const dashboardsAdapter = createEntityAdapter<Dashboard.Data>({
  sortComparer: (a, b) => a.createdAt.localeCompare(b.createdAt),
});

const initialState = dashboardsAdapter.getInitialState<
  Store.InitialState & {
    recentIds: Dashboard.Data["id"][];
    hasLoadedAll: boolean;
    lastVisibleDoc: string | null;
  }
>({
  status: "idle",
  error: null,
  recentIds: [],
  hasLoadedAll: false,
  lastVisibleDoc: null,
});

export const fetchAllDashboards = createAsyncThunk<
  Dashboard.Data[],
  Company.Data["id"],
  { state: Store.RootState }
>("dashboards/fetch-all-by-company-id", api.getAllDashboards);

export const fetchAllDashboardsFromGlobalSearch = createAsyncThunk<
  Dashboard.Data[],
  { companyId: Company.Data["id"]; skipGlobalLoader?: boolean },
  { state: Store.RootState }
>(
  "dashboards/fetch-all-from-global-search-by-company-id",
  async ({ companyId }) => {
    return api.getAllDashboards(companyId);
  },
);

export const fetchRecentDashboards = createAsyncThunk<
  {
    dashboards: Dashboard.Data[];
    lastVisibleDoc: string | null;
    hasLoadedAll: boolean;
  },
  {
    companyId: Company.Data["id"];
    limit?: number;
    lastVisibleDoc?: string | null;
    isLoadMore?: boolean;
  },
  { state: Store.RootState }
>("dashboards/fetch-recent-by-company-id", async (payload) => {
  return api.getRecentDashboards(payload);
});

export const fetchDashboardById = createAsyncThunk<
  Dashboard.Data,
  Dashboard.Data["id"],
  { state: Store.RootState }
>("dashboards/fetch-by-id", api.getDashboardById);

export const createDashboard = createAsyncThunk<
  Dashboard.Data,
  Dashboard.DataForCreation,
  { state: Store.RootState }
>("dashboards/create-one", async (payload, { getState, dispatch }) => {
  const state = getState();

  const dashboardPayload = createDashboardPayload(state, payload);

  const result = await api.createDashboard(dashboardPayload);

  await dispatch(
    createDashboardDefaultDateRanges({
      authorId: dashboardPayload.authorId,
      companyId: dashboardPayload.companyId,
      types: Array.from(DASHBOARD_DATE_RANGE_TYPES),
      trackersCollectionId: result.trackersCollectionId,
    }),
  );

  return result;
});

export const updateDashboard = createAsyncThunk<
  Store.UpdateEntity<Dashboard.Data>,
  Store.UpdateEntity<Dashboard.Data>
>("dashboards/update-one", (payload) => {
  const formattedChanges = omit(payload.changes, "expandedWidgetId");

  const formattedPayload = { ...payload, changes: formattedChanges };

  return api.updateDashboard(formattedPayload);
});

export const updateDashboards = createAsyncThunk<
  Store.UpdateEntity<Dashboard.Data>[],
  Store.UpdateEntity<Dashboard.Data>[]
>("dashboards/update-many", api.updateDashboards);

export const updateDashboardsByAuthorId = createAsyncThunk<
  Store.UpdateEntity<Dashboard.Data>[],
  {
    changes: Store.UpdateEntity<Dashboard.Data>["changes"];
    authorId: Dashboard.Data["authorId"];
  },
  { state: Store.RootState }
>("dashboards/update-by-author-id", (payload, { getState }) => {
  const state = getState();

  const companyId = selectCompanyId(state);

  return api.updateDashboardsByAuthorId(payload, companyId);
});

export const removeDashboard = createAsyncThunk<
  Dashboard.Data["id"],
  Dashboard.Data["id"]
>("dashboards/remove-one", async (payload) => {
  await api.deleteDashboard(payload);

  return payload;
});

export const removeDashboards = createAsyncThunk<
  Dashboard.Data["id"][],
  Dashboard.Data["id"][]
>("dashboards/remove-many", async (payload) => {
  await api.deleteDashboards(payload);

  return payload;
});

export const duplicateDashboard = createAsyncThunk<
  Dashboard.Data[],
  {
    trackerIds: Tracker.Data["id"][];
    visibility: Dashboard.Visibility;
    dashboardId: Dashboard.Data["id"];
    configurations: Dashboard.DuplicateConfig[];
    shouldCopyKeywords: boolean;
  },
  { state: Store.RootState; dispatch: Store.AppDispatch }
>("dashboards/duplicate-dashboard", async (payload, { getState, dispatch }) => {
  const state = getState();

  const [companyId, userId] = [selectCompanyId(state), selectUserId(state)];

  const { dashboards, trackers, trackersCollections } =
    await api.duplicateDashboard({ ...payload, userId, companyId });

  dispatch(addTrackers(trackers));
  dispatch(addTrackersCollections(trackersCollections));

  return dashboards;
});

const dashboardsSlice = createSlice({
  name: "dashboards",
  initialState,
  reducers: {
    setDashboardsHasLoadedAll: (state, action) => {
      state.hasLoadedAll = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllDashboards.pending, onStatePending);
    builder.addCase(fetchAllDashboards.rejected, onStateRejected);
    builder.addCase(fetchAllDashboards.fulfilled, (...args) => {
      dashboardsAdapter.addMany(...args);
      onStateFulfilled(...args);
      args[0].hasLoadedAll = true;
    });

    builder.addCase(
      fetchAllDashboardsFromGlobalSearch.fulfilled,
      (state, action) => {
        dashboardsAdapter.addMany(state, action.payload);
        state.error = null;
        state.status = "succeeded";
        state.hasLoadedAll = true;
      },
    );
    builder.addCase(
      fetchAllDashboardsFromGlobalSearch.rejected,
      onStateRejected,
    );

    builder.addCase(fetchRecentDashboards.pending, (state, action) => {
      if (!action.meta.arg.isLoadMore) {
        state.status = "loading";
      }
      state.error = null;
    });
    builder.addCase(fetchRecentDashboards.rejected, onStateRejected);
    builder.addCase(fetchRecentDashboards.fulfilled, (state, action) => {
      dashboardsAdapter.addMany(state, action.payload.dashboards);
      state.lastVisibleDoc = action.payload.lastVisibleDoc;
      state.hasLoadedAll = action.payload.hasLoadedAll;
      state.recentIds = [
        ...state.recentIds,
        ...action.payload.dashboards.map((d) => d.id),
      ];
      state.status = "succeeded";
    });

    builder.addCase(fetchDashboardById.fulfilled, dashboardsAdapter.addOne);

    builder.addCase(createDashboard.fulfilled, dashboardsAdapter.addOne);

    builder.addCase(updateDashboard.fulfilled, dashboardsAdapter.updateOne);

    builder.addCase(updateDashboards.fulfilled, dashboardsAdapter.updateMany);

    builder.addCase(
      updateDashboardsByAuthorId.fulfilled,
      dashboardsAdapter.updateMany,
    );

    builder.addCase(removeDashboard.fulfilled, dashboardsAdapter.removeOne);

    builder.addCase(removeDashboards.fulfilled, dashboardsAdapter.removeMany);

    builder.addCase(duplicateDashboard.fulfilled, dashboardsAdapter.addMany);
  },
});

export const { setDashboardsHasLoadedAll } = dashboardsSlice.actions;

export default dashboardsSlice.reducer;
